/** Cláusulas WHERE compartidas entre búsqueda POS y listado admin (prefijo `p.` vs columnas bare). */

import { sqlCategoryComparableValuesForFilter } from '../domain/categoryTypes';

export const catalogCol = (prefix: string, column: string) => (prefix ? `${prefix}${column}` : column);

export const catalogSearchFields = (prefix: string) => [
  `COALESCE(${catalogCol(prefix, 'name')}, '')`,
  `COALESCE(${catalogCol(prefix, 'sku')}, '')`,
  `COALESCE(${catalogCol(prefix, 'barcode')}, ${catalogCol(prefix, 'sku')}, '')`,
  `COALESCE(${catalogCol(prefix, 'brand')}, '')`,
];

export type CatalogItemTypeSqlFilter = 'all' | 'physical' | 'service' | undefined;

export type CatalogStatusSqlFilter = 'all' | 'active' | 'inactive';

export function appendStatusFilter(
  clauses: string[],
  status: CatalogStatusSqlFilter,
  prefix: string
): void {
  const col = catalogCol(prefix, 'is_active');
  if (status === 'active') {
    clauses.push(`COALESCE(${col}, 1) = 1`);
  } else if (status === 'inactive') {
    clauses.push(`COALESCE(${col}, 1) = 0`);
  }
}

export function appendCategoryFilter(
  clauses: string[],
  params: Array<string | number>,
  category: string | 'all',
  prefix: string
): void {
  if (category === 'all') return;
  const categoryValues = sqlCategoryComparableValuesForFilter(category);
  if (categoryValues.length === 0) {
    clauses.push('1 = 0');
  } else {
    const col = catalogCol(prefix, 'category_id');
    clauses.push(`TRIM(LOWER(COALESCE(${col}, ''))) IN (${categoryValues.map(() => '?').join(', ')})`);
    params.push(...categoryValues);
  }
}

export function appendItemTypeFilter(
  clauses: string[],
  itemType: CatalogItemTypeSqlFilter,
  prefix: string
): void {
  if (itemType === 'physical') {
    clauses.push(`(COALESCE(${catalogCol(prefix, 'item_type')}, 'physical') = 'physical')`);
  } else if (itemType === 'service') {
    clauses.push(`${catalogCol(prefix, 'item_type')} = 'service'`);
  }
}

export function appendBrandFilter(
  clauses: string[],
  params: Array<string | number>,
  brandId: string | null | undefined,
  prefix: string
): void {
  const brandIdVal = brandId != null && String(brandId).trim() !== '' ? String(brandId).trim() : '';
  if (brandIdVal) {
    clauses.push(`coalesce(${catalogCol(prefix, 'brand_id')}, '') = ?`);
    params.push(brandIdVal);
  }
}

export function appendSupplierFilter(
  clauses: string[],
  params: Array<string | number>,
  supplierId: string | null | undefined,
  prefix: string
): void {
  const supplierTrim =
    supplierId != null && String(supplierId).trim() !== '' ? String(supplierId).trim() : '';
  if (supplierTrim) {
    clauses.push(`coalesce(${catalogCol(prefix, 'preferred_supplier_id')}, '') = ?`);
    params.push(supplierTrim);
  }
}

// ─── Stock join + alert/incomplete filters (catálogo admin) ─────────────────

/**
 * SQL para el LEFT JOIN de stocks. Requiere el alias `stock` en el WHERE.
 * Cuando branchId es null, produce un join vacío (todas las columnas stock.* son NULL)
 * — consistente con el comportamiento client-side (stockQuantity == null → "sin stock cargado").
 */
export function buildCatalogStockJoin(branchId: string | null): { sql: string; params: Array<string | number> } {
  if (!branchId) {
    return {
      sql: `LEFT JOIN (SELECT NULL AS product_id, NULL AS stock_quantity, NULL AS stock_min_quantity, NULL AS stock_avg_cost WHERE 1=0) stock ON stock.product_id = ${catalogCol('', 'id')}`,
      params: [],
    };
  }
  return {
    sql: `LEFT JOIN (
           SELECT
             product_id,
             SUM(quantity) AS stock_quantity,
             MAX(min_quantity) AS stock_min_quantity,
             CASE
               WHEN SUM(quantity) > 0 THEN SUM(quantity * COALESCE(avg_cost, 0)) / SUM(quantity)
               ELSE 0
             END AS stock_avg_cost
           FROM stocks
           WHERE branch_id = ? AND variant_id IS NULL
           GROUP BY product_id
         ) stock ON stock.product_id = ${catalogCol('', 'id')}`,
    params: [branchId],
  };
}

/**
 * Filtro "Solo alertas de stock": productos físicos activos con señal de stock warning/danger.
 * Replica catalogProductHasInventoryAlert + getCatalogProductStockSignal.
 * Requiere que el stock join esté presente (alias `stock`).
 */
export function appendAlertFilter(clauses: string[], prefix: string): void {
  const p = prefix;
  clauses.push(`COALESCE(${catalogCol(p, 'is_active')}, 1) = 1`);
  clauses.push(`COALESCE(${catalogCol(p, 'item_type')}, 'physical') = 'physical'`);
  clauses.push(`(
    stock.stock_quantity IS NULL
    OR stock.stock_quantity <= 0
    OR (stock.stock_min_quantity > 0 AND stock.stock_quantity <= stock.stock_min_quantity)
    OR (stock.stock_avg_cost > 0 AND (${catalogCol(p, 'price')} - stock.stock_avg_cost) < 0)
    OR (COALESCE(stock.stock_avg_cost, 0) <= 0 AND stock.stock_quantity > 0)
  )`);
}

/**
 * Filtro "Solo productos incompletos": sin código, sin categoría, o sin costo (físicos).
 * Replica getProductCompletenessIssues.
 * Requiere que el stock join esté presente (alias `stock`).
 */
export function appendIncompleteFilter(clauses: string[], prefix: string): void {
  const p = prefix;
  clauses.push(`(
    (TRIM(IFNULL(${catalogCol(p, 'barcode')}, '')) = '' AND TRIM(IFNULL(${catalogCol(p, 'sku')}, '')) = '')
    OR TRIM(IFNULL(${catalogCol(p, 'category_id')}, '')) = ''
    OR (COALESCE(${catalogCol(p, 'item_type')}, 'physical') = 'physical' AND (stock.stock_avg_cost IS NULL OR stock.stock_avg_cost <= 0))
  )`);
}
